// CommandMenu.jsx
import React, {
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
} from 'react';
import { Command } from 'cmdk';
import * as Dialog from '@radix-ui/react-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import './CmdMenu.css';
import { AppContext } from '../AppContext.tsx';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  useCommandMenu,
  formatKeySequence,
  getCategoryIcon,
} from '../../hooks/useCommandMenu.js';
import Spinner from '../Spinner.jsx';
import { getCommandIcon } from '../../utils/commandIcons.js';
import KeyboardShortcut from '../KeyboardShortcut.jsx';
import { useEncryptedSearch } from '../../hooks/useEncryptedSearch.ts';

/* ---------- small helpers ------------------------------------------------ */

const isSameDay = (d1, d2) =>
  d1.getFullYear() === d2.getFullYear() &&
  d1.getMonth() === d2.getMonth() &&
  d1.getDate() === d2.getDate();

const getTimeCategory = (timestamptzString) => {
  const inputDate = new Date(timestamptzString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(today.getDate() - 7);

  if (isSameDay(inputDate, today)) return 'Today';
  if (isSameDay(inputDate, yesterday)) return 'Yesterday';
  if (inputDate >= weekAgo) return 'Past week';
  return inputDate.toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
};

const useDebounce = (value, delay = 400) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
};

/* ---------- memoised rows ------------------------------------------------- */

const CommandRow = React.memo(function CommandRow({
  command,
  shortcut,
  onSelect,
}) {
  const IconComponent = getCommandIcon(command.description);
  
  return (
    <Command.Item
      key={command.sequence}
      disabled={!command.enabled}
      onSelect={onSelect}
      aria-label={
        command.enabled
          ? `${command.description} - Keyboard shortcut: ${command.sequence}`
          : `${command.description} - Disabled: ${command.disabledReason}`
      }
      className={command.enabled ? '' : 'opacity-50 cursor-not-allowed'}
    >
      <div className="flex flex-col items-start gap-[2px] flex-1">
        <div className="flex flex-row items-center gap-[8px] w-full">
          <span className="flex flex-row items-center gap-[8px]">
            {IconComponent && (
              <span className="inline-flex items-center justify-center w-[14px] h-[14px] flex-shrink-0">
                <IconComponent 
                  className="w-[14px] h-[14px]"
                  aria-hidden="true"
                />
              </span>
            )}
            {command.description}
          </span>
        </div>
        {!command.enabled && command.disabledReason && (
          <span className="text-xs text-secondary italic">
            {command.disabledReason}
          </span>
        )}
      </div>
      <div aria-hidden="true" className={command.enabled ? '' : 'opacity-50'}>
        <KeyboardShortcut sequence={shortcut} />
      </div>
    </Command.Item>
  );
});

const ConversationRow = React.memo(function ConversationRow({
  m,
  timeLabel,
  onSelect,
}) {
  return (
    <Command.Item
      key={m.conversationId}
      value={m.conversationId}
      onSelect={onSelect}
      aria-label={`Conversation: ${m.title || 'New Conversation'} - ${timeLabel}`}
    >
      <span className="truncate flex-1">{m.title || 'New Conversation'}</span>
      <span className="text-secondary text-sm ml-2" aria-hidden="true">
        {timeLabel}
      </span>
    </Command.Item>
  );
});

/* ---------- main component ------------------------------------------------ */

function CommandMenu() {
  /* context & routing ----------------------------------------------------- */
  const {
    conversations,
    favoriteConversations,
    searchConversations,
    searchModalActive,
    setSearchModal,
    toggleModelSelector,
    createNewConversation,
    toggleSidebar,
  } = useContext(AppContext);

  const navigate     = useNavigate();
  const location     = useLocation(); // if you really need it somewhere
  const commandsHook = useCommandMenu();

  // Encrypted search for client-side searching of encrypted conversations
  const allConversations = useMemo(
    () => [...conversations, ...favoriteConversations],
    [conversations, favoriteConversations]
  );
  const hasEncryptedConversations = useMemo(
    () => allConversations.some((c) => c.encrypted),
    [allConversations]
  );
  const encryptedSearch = useEncryptedSearch(
    allConversations.filter((c) => c.encrypted)
  );

  /* local state ----------------------------------------------------------- */
  const [query, setQuery]                 = useState('');
  const [loading, setLoading]             = useState(false);
  const [convResults, setConvResults]     = useState(conversations);

  const debouncedQuery = useDebounce(query, 400);

  /* ----------------------------------------------------------------------- */
  /* 1. commands filtering (pure, no setState)                               */
  const filteredCommands = useMemo(() => {
    const q = query.trim().toLowerCase();

    const matchesQuery = (cmd) => {
      if (!q) return true;
      const { description, sequence } = cmd;
      if (description.toLowerCase().includes(q)) return true;
      if (sequence.toLowerCase().includes(q)) return true;
      return sequence.split(' ').some((k) => k.toLowerCase().includes(q));
    };

    const result = {};
    for (const [cat, cmds] of Object.entries(commandsHook.byCategory)) {
      const arr = cmds
        .map((c) => ({
          ...c,
          enabled: !c.when || c.when(),
        }))
        .filter((c) => c.enabled && matchesQuery(c));

      if (arr.length) result[cat] = arr;
    }
    return result;
  }, [query, commandsHook.byCategory]);

  /* 2. key-sequence cache (so split+map happens once)                       */
  const keySeqCache = useMemo(() => {
    const map = new Map();
    Object.values(filteredCommands).flat().forEach((c) => {
      map.set(c.sequence, formatKeySequence(c.sequence));
    });
    return map;
  }, [filteredCommands]);

  /* 3. conversations search ------------------------------------------------ */
  useEffect(() => {
    // cleared field
    if (!debouncedQuery.trim()) {
      setLoading(false);
      setConvResults(conversations);
      return;
    }

    setLoading(true);
    let cancelled = false;

    (async () => {
      try {
        // Server-side search (unencrypted conversations)
        const serverResults = await searchConversations(debouncedQuery);

        // Client-side encrypted search (if vault is unlocked)
        let encryptedResults = [];
        if (encryptedSearch.isEncryptionEnabled) {
          encryptedResults = await encryptedSearch.search(debouncedQuery);
        }

        if (!cancelled) {
          // Merge and deduplicate by conversationId
          const seen = new Set(serverResults.map((r) => r.conversationId));
          const merged = [...serverResults];
          for (const r of encryptedResults) {
            if (!seen.has(r.conversationId)) {
              merged.push(r);
            }
          }

          // Sort by most recent first
          merged.sort(
            (a, b) =>
              new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
          );

          setConvResults(merged);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, searchConversations, conversations, encryptedSearch]);

  /* event handlers -------------------------------------------------------- */
  const handleInputChange = (val) => {
    setQuery(val);
    setLoading(val !== '');
  };

  const handleCommandSelect = useCallback(
    (cmd) => {
      if (!cmd.enabled) return;
      cmd.handler();
      setSearchModal(false);
    },
    [setSearchModal]
  );

  const handleConversationSelect = useCallback(
    (id) => {
      navigate(`/chat/${id}`);
      setSearchModal(false);
    },
    [navigate, setSearchModal]
  );

  /* render ---------------------------------------------------------------- */
  return (
    <>
      {searchModalActive && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
          onClick={() => setSearchModal(false)}
        />
      )}

      <Command.Dialog
        loop
        shouldFilter={false}
        open={searchModalActive}
        onOpenChange={setSearchModal}
        label="Global command menu"
      >
        <VisuallyHidden.Root>
          <Dialog.Title>Command Menu</Dialog.Title>
          <Dialog.Description>
            Search for commands and conversations. Use arrow keys to navigate and Enter to select.
          </Dialog.Description>
        </VisuallyHidden.Root>

        <Command.Input
          placeholder="Type a command or search through conversations…"
          onValueChange={handleInputChange}
          aria-label="Search commands and conversations"
        />
        <Command.Separator alwaysRender role="separator" />

        <Command.List aria-label="Search results">
          {/* ---------- commands ------------------------------------------- */}
          {Object.entries(filteredCommands).map(([cat, cmds]) => (
            <Command.Group key={cat} heading={cat}>
              {cmds.map((cmd) => (
                <CommandRow
                  key={cmd.sequence}
                  command={cmd}
                  shortcut={keySeqCache.get(cmd.sequence)}
                  onSelect={() => handleCommandSelect(cmd)}
                />
              ))}
            </Command.Group>
          ))}

          {/* ---------- conversations -------------------------------------- */}
          {(loading || convResults.length > 0) && (
            <Command.Group heading="Conversations">
              {loading ? (
                <div className="flex items-center py-2 px-2">
                  <Spinner size="sm" color="text-secondary" variant="ring" />
                </div>
              ) : (
                convResults.map((m) => (
                  <ConversationRow
                    key={m.conversationId}
                    m={m}
                    timeLabel={getTimeCategory(m.updatedAt)}
                    onSelect={() => handleConversationSelect(m.conversationId)}
                  />
                ))
              )}
            </Command.Group>
          )}

          {/* ---------- empty state ---------------------------------------- */}
          {Object.keys(filteredCommands).length === 0 &&
            convResults.length === 0 &&
            query.trim() &&
            !loading && (
              <div className="py-2 px-2 text-secondary">
                <div className="text-sm">No results found</div>
                <div className="text-xs mt-1 opacity-75">
                  Try a different search term
                </div>
              </div>
            )}

          {/* ---------- encrypted search hint ------------------------------ */}
          {query.trim() &&
            !encryptedSearch.isEncryptionEnabled &&
            hasEncryptedConversations && (
              <div className="py-2 px-2 text-secondary text-xs opacity-75">
                Unlock your vault to search encrypted conversations
              </div>
            )}
        </Command.List>
      </Command.Dialog>
    </>
  );
}

export default CommandMenu;
